type StorageType = 'local' | 'session';

type BrowserStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

type StorageOptions = {
  order?: readonly StorageType[];
  includeMemory?: boolean;
};

const DEFAULT_ORDER: StorageType[] = ['local', 'session'];
const STORAGE_PROBE_KEY = '__loan_storage_probe__';
const memoryStore = new Map<string, string>();

const memoryStorage: BrowserStorage = {
  getItem: (key) => memoryStore.get(key) ?? null,
  setItem: (key, value) => {
    memoryStore.set(key, value);
  },
  removeItem: (key) => {
    memoryStore.delete(key);
  },
};

const getWindowStorage = (type: StorageType): BrowserStorage | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const storage = type === 'local' ? window.localStorage : window.sessionStorage;
    storage.setItem(STORAGE_PROBE_KEY, '1');
    storage.removeItem(STORAGE_PROBE_KEY);
    return storage;
  } catch {
    return null;
  }
};

export const getBrowserStorages = (options?: StorageOptions): BrowserStorage[] => {
  const storages: BrowserStorage[] = [];
  const order = options?.order?.length ? options.order : DEFAULT_ORDER;

  for (const type of order) {
    const storage = getWindowStorage(type);
    if (storage && !storages.includes(storage)) {
      storages.push(storage);
    }
  }

  if (options?.includeMemory !== false) {
    storages.push(memoryStorage);
  }

  return storages;
};

export const readBrowserStorageItem = (key: string, options?: StorageOptions) => {
  for (const storage of getBrowserStorages(options)) {
    const value = storage.getItem(key);
    if (value !== null) {
      return value;
    }
  }
  return null;
};

export const writeBrowserStorageItem = (key: string, value: string, options?: StorageOptions) => {
  for (const storage of getBrowserStorages(options)) {
    storage.setItem(key, value);
  }
};

export const removeBrowserStorageItem = (key: string, options?: StorageOptions) => {
  for (const storage of getBrowserStorages(options)) {
    storage.removeItem(key);
  }
};
